import { Reveal } from "./Reveal";
import { Link } from "react-router-dom";
import { Star } from "./Logo";
import { useLang } from "@/i18n/LanguageContext";
import { Shirt, Sparkles, CalendarDays, BarChart3, Plane, ScanSearch, ListChecks, GalleryHorizontalEnd, BookOpen, Wand2, Store, Check, Minus } from "lucide-react";

const ICONS = [Shirt, Sparkles, CalendarDays, BarChart3, Plane, ScanSearch, ListChecks, GalleryHorizontalEnd, BookOpen, Wand2, Store];

export default function Wardrobe() {
  const { t } = useLang();
  const w = t.wardrobe;
  if (!w) return null;

  return (
    <section id="garderobe" className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28" data-testid="wardrobe-section">
      <Reveal>
        <p className="flex items-center gap-2 text-[11px] font-black uppercase" style={{ letterSpacing: "0.24em", color: "var(--camel)" }}>
          <Star size={11} /> {w.kicker}
        </p>
        <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight" data-testid="wardrobe-title">{w.title}</h2>
        <p className="mt-4 max-w-2xl text-base md:text-lg" style={{ color: "var(--muted-warm)" }}>{w.sub}</p>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {w.items.map((item, i) => {
          const Icon = ICONS[i];
          return (
            <Reveal
              key={i}
              delay={(i % 5) * 0.05}
              className="rounded-2xl border p-5 transition-shadow duration-500 hover:shadow-[0_18px_36px_-20px_rgba(198,156,109,0.4)]"
              style={{ background: "var(--surface)", borderColor: "var(--line)" }}
              data-testid={`wardrobe-card-${i + 1}`}
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: "var(--rose-soft)" }}>
                <Icon size={17} strokeWidth={1.7} style={{ color: "var(--camel)" }} />
              </span>
              <h3 className="mt-3.5 text-sm font-semibold leading-snug">{item.t}</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed" style={{ color: "var(--muted-warm)" }}>{item.d}</p>
            </Reveal>
          );
        })}
      </div>

      <Reveal delay={0.1} className="mt-14">
        <p className="text-center text-sm font-semibold" style={{ color: "var(--camel)" }} data-testid="wardrobe-claim">✦ {w.claim}</p>
        <div className="mx-auto mt-6 max-w-3xl overflow-hidden rounded-3xl border" style={{ borderColor: "var(--line)", background: "var(--surface)" }} data-testid="wardrobe-plans">
          <div className="grid grid-cols-[1.2fr_1fr_1.2fr] items-center border-b px-5 py-4 sm:px-7" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }}>
            <span className="text-xs font-bold uppercase" style={{ letterSpacing: "0.14em", color: "var(--muted-warm)" }}>{w.plans.title}</span>
            <span className="text-center text-sm font-bold">{w.plans.free}</span>
            <span className="flex flex-col items-center gap-1 text-center">
              <span className="text-sm font-bold" style={{ color: "var(--camel)" }}>{w.plans.plus}</span>
              <span className="rounded-full px-2.5 py-0.5 text-[10px] font-bold text-white" style={{ background: "var(--gold)" }}>{w.plans.badge}</span>
            </span>
          </div>
          {w.plans.rows.map((row, i) => (
            <div
              key={i}
              className="grid grid-cols-[1.2fr_1fr_1.2fr] items-center px-5 py-3.5 text-sm sm:px-7"
              style={{ borderTop: i === 0 ? "none" : "1px solid var(--line)" }}
              data-testid={`wardrobe-plan-row-${i + 1}`}
            >
              <span className="font-medium">{row.l}</span>
              <span className="text-center text-[13px]" style={{ color: "var(--muted-warm)" }}>
                {row.f === "–" ? <Minus size={14} className="mx-auto opacity-50" /> : row.f}
              </span>
              <span className="flex items-center justify-center gap-1.5 text-center text-[13px] font-semibold" style={{ color: "var(--camel)" }}>
                {i > 1 && <Check size={13} className="shrink-0" />}
                {row.p}
              </span>
            </div>
          ))}
        </div>
        <p className="mx-auto mt-4 max-w-2xl text-center text-xs leading-relaxed" style={{ color: "var(--muted-warm)" }} data-testid="wardrobe-plans-note">{w.plans.note}</p>
        {w.plans.cta && (
          <p className="mt-5 text-center">
            <Link to="/mira-plus" data-testid="wardrobe-plus-link" className="gold-underline text-sm font-semibold" style={{ color: "var(--camel)" }}>
              {w.plans.cta} →
            </Link>
          </p>
        )}
      </Reveal>
    </section>
  );
}
